// ============================================================================
// champsAutorite.js — miroir client de la règle FB10a (firestore.rules,
// 30/09/2026) : les champs d'une fiche delegates/ qui ouvrent des droits côté
// serveur ne CHANGENT que sous la main d'un administrateur authentifié, et
// l'e-mail et les alias aussi (clé de connexion et d'habilitation : notes de
// frais, présences), même pour l'intéressé (relecture du 30/09/2026). Une
// réécriture qui ne change rien passe toujours.
//
// Ce module sert à deux choses :
//   • preserverAutorite : saveDelegate reprend, pour une session qui n'est pas
//     administratrice, les valeurs EN BASE de ces champs (copie périmée d'un
//     écran, repli sur la session) au lieu de se faire refuser l'écriture ;
//   • autoriteInchangee / identiteInchangee : le même prédicat que la règle,
//     testé cas par cas (src/champsAutorite.test.js).
// Toute modification de la liste se fait AUX DEUX ENDROITS.
//
// S3 (décision de Pierre du 30/09/2026) : « les administrateurs, plus chacun
// pour ses propres assistantes ». `assistantes` reste un champ d'autorité
// (repris en base par défaut), mais l'intéressé authentifié le change sur SA
// fiche : la règle le juge à part (assistantesInchangees ||
// assistantesParLInteresse). Un DG ou un président ne le change pas pour un
// collègue.
//
// 12/10/2026 (décision de Pierre, puis relecture adverse) : la branche de
// l'administrateur exige en plus, hors super-administrateur, que la qualité
// d'administrateur ne change pas (changeQualiteAdmin, src/retraitRoleAdmin.js),
// que l'identité d'un administrateur en exercice ne change pas, et que
// `actif` et `supprime` soient des booléens (ou absents, ou nuls) :
// gesteAdministrateurPermis de la règle.
// ============================================================================
import { changeQualiteAdmin, estFicheAdministrateur } from "./retraitRoleAdmin";

// Champ → valeurs « vides » qui se valent (absent vaut null). `actif` : absent
// vaut ACTIF, son vide est donc `true`, jamais `false`.
export const VIDES_AUTORITE = {
  role: [null, ""],
  roles: [null, "", []],
  isSuperAdmin: [null, false],
  actif: [null, true],
  supprime: [null, false],
  technique: [null, false],
  mutuelle: [null, ""],
  mutuelleGouvernance: [null, ""],
  assistantes: [null, []],
  mandatCA: [null, {}],
  mandatAG: [null, {}],
  mandatBureau: [null, {}],
  mandatCODIR: [null, {}],
  mandatENG: [null, false],
  mandatsHistorique: [null, []],
};
export const CHAMPS_AUTORITE = Object.keys(VIDES_AUTORITE);
export const CHAMPS_IDENTITE = ["email", "emailAliases"];
const VIDES_IDENTITE = { email: [null, ""], emailAliases: [null, []] };

// Égalité profonde, indifférente à l'ordre des clés (comme la règle).
function egal(a, b) {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) return a.length === b.length && a.every((x, i) => egal(x, b[i]));
  const ka = Object.keys(a), kb = Object.keys(b);
  return ka.length === kb.length && ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && egal(a[k], b[k]));
}
const lire = (o, k) => (o && o[k] !== undefined ? o[k] : null);
const dansVides = (v, vides) => vides.some((x) => egal(v, x));

export function valeurInchangee(avant, apres, k, vides) {
  const a = lire(avant, k), b = lire(apres, k);
  return egal(a, b) || (dansVides(a, vides) && dansVides(b, vides));
}
export function autoriteInchangee(avant, apres) {
  return CHAMPS_AUTORITE.every((k) => valeurInchangee(avant, apres, k, VIDES_AUTORITE[k]));
}
export function identiteInchangee(avant, apres) {
  return CHAMPS_IDENTITE.every((k) => valeurInchangee(avant, apres, k, VIDES_IDENTITE[k]));
}

/** Le champ que l'intéressé authentifié change sur SA fiche (S3). */
export const CHAMP_ASSISTANTES = "assistantes";
/** Au plus 10 assistant·e·s par fiche (même borne que la règle). */
export const MAX_ASSISTANTES = 10;

/** Tous les champs d'autorité sauf `assistantes` : ceux-là restent aux
 *  administrateurs, même pour l'intéressé. */
export const CHAMPS_AUTORITE_HORS_ASSISTANTES = CHAMPS_AUTORITE.filter((k) => k !== CHAMP_ASSISTANTES);
export function autoriteHorsAssistantesInchangee(avant, apres) {
  return CHAMPS_AUTORITE_HORS_ASSISTANTES.every((k) => valeurInchangee(avant, apres, k, VIDES_AUTORITE[k]));
}
export function assistantesInchangees(avant, apres) {
  return valeurInchangee(avant, apres, CHAMP_ASSISTANTES, VIDES_AUTORITE[CHAMP_ASSISTANTES]);
}

/** Reprend sur `fiche` les valeurs EN BASE (`avant`) des champs d'autorité
 *  (si `autorite` est faux) et d'identité (si `identite` est faux). Un champ
 *  absent en base est retiré. `assistantes` suit `autorite`, sauf si
 *  l'option `assistantes` le laisse passer (l'intéressé sur SA fiche, S3).
 *  Rend une nouvelle fiche. */
export function preserverAutorite(fiche, avant, { autorite = false, identite = false, assistantes = autorite } = {}) {
  if (!avant || !fiche) return fiche;
  const out = { ...fiche };
  const reprendre = (k) => {
    if (avant[k] !== undefined) out[k] = avant[k];
    else delete out[k];
  };
  if (!autorite) CHAMPS_AUTORITE.forEach((k) => { if (k !== CHAMP_ASSISTANTES || !assistantes) reprendre(k); });
  else if (!assistantes) reprendre(CHAMP_ASSISTANTES);
  if (!identite) CHAMPS_IDENTITE.forEach(reprendre);
  return out;
}

/** `actif` et `supprime` : booléens, absents ou nuls (statutBienType de la
 *  règle). Les portes de connexion lisent `supprime` comme un drapeau. */
export function statutBienType(fiche) {
  return ["actif", "supprime"].every((k) => [true, false, null].includes(lire(fiche, k)));
}

/** L'identité (e-mail, alias) d'un administrateur EN EXERCICE change-t-elle ?
 *  Elle ne change que par le super-administrateur (sa propre fiche comprise). */
export function identiteAdminChange(avant, apres) {
  return estFicheAdministrateur(avant) && !identiteInchangee(avant, apres);
}

/** Miroir de la règle d'update de delegates/ pour FB10a, S3 et le 12/10/2026
 *  (hors verrous plus anciens : identifiants, superadmin, e-mail du
 *  super-admin). `auteur` = { uid, aramis, fiche, superAdmin } : la session
 *  Firebase, la fiche EN BASE de son titulaire (null sans session) et, pour
 *  le super-administrateur (identifiant de superadmin/unique), `superAdmin`.
 *  `docId` = la fiche modifiée. */
export function ecritureFicheAutorisee({ docId = null, avant, apres, auteur = null }) {
  const jeton = !!(auteur && auteur.uid && auteur.aramis === true);
  const f = jeton ? auteur.fiche : null;
  const admin = !!(f && ["admin", "superadmin"].includes(f.role) && f.actif !== false && f.supprime !== true);
  // `get('assistantes', []).size() <= 10` : taille d'une liste, d'un texte ou
  // d'une carte ; absent vaut [] ; null, un nombre ou un booléen font échouer
  // la règle. Ni le type ni l'état de la fiche ne sont vérifiés (sans effet :
  // voir firestore.rules, assistantesParLInteresse).
  const a = apres ? apres[CHAMP_ASSISTANTES] : undefined;
  const taille = a === undefined ? 0
    : (Array.isArray(a) || typeof a === "string") ? a.length
    : (a && typeof a === "object") ? Object.keys(a).length : Infinity;
  const interesse = jeton && !!docId && auteur.uid === docId && taille <= MAX_ASSISTANTES;
  // (A && B) || (E ? gesteAdministrateurPermis : false), comme la règle.
  const sansAutorite = autoriteHorsAssistantesInchangee(avant, apres)
    && (assistantesInchangees(avant, apres) || interesse) && identiteInchangee(avant, apres);
  const superAdmin = jeton && auteur.superAdmin === true;
  const gesteAdmin = admin && statutBienType(apres)
    && (superAdmin || (!changeQualiteAdmin(avant, apres) && !identiteAdminChange(avant, apres)));
  return sansAutorite || gesteAdmin;
}
